import React, { useState, useEffect, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ShoppingCart, Heart, Check, ChevronLeft, ChevronRight } from 'lucide-react';
import api from '../utils/api';
import { useCart } from '../contexts/CartContext';

interface Product {
  _id: string;
  name: string;
  description: string;
  price: number;
  category: string;
  images: string[];
  colors?: string[];
  features: string[];
  specifications: Record<string, any>;
}

const ProductPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(0);
  const [selectedColor, setSelectedColor] = useState<string>('');
  const [colorError, setColorError] = useState('');
  const [isAddingToCart, setIsAddingToCart] = useState(false);
  const [recommendedProducts, setRecommendedProducts] = useState<Product[]>([]);
  const [loadingRecommendations, setLoadingRecommendations] = useState(false);
  
  const { addItem } = useCart();

  // Add the same image URL handling logic as ProductCard
  const getImageUrl = (imagePath: string) => {
    if (!imagePath || imagePath === 'undefined' || imagePath === 'null') {
      return 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgdmlld0JveD0iMCAwIDEwMCAxMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2Zz4KPHJlY3Qgd2lkdGg9IjEwMCIgaGVpZ2h0PSIxMDAiIGZpbGw9IiNCM0IzQjMiLz4KPHBhdGggZD0iTTMwIDMwSDcwVjcwSDMwVjMwWiIgZmlsbD0iI0QxRDVEQiIvPgo8cGF0aCBkPSJNNDAgNDBIMjBWNjBIMjBWNDBaIiBmaWxsPSIjQzdDQ0Q3Ii8+Cjwvc3ZnPgo=';
    }
    
    // If it's already a data URL or external URL, return as is
    if (imagePath.startsWith('http') || imagePath.startsWith('data:')) {
      return imagePath;
    }
    
    // Handle file paths - try direct uploads path first
    const normalizedPath = imagePath.replace(/\\/g, '/');
    const filename = normalizedPath.split('/').pop();
    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
    const directUrl = `${apiUrl}/uploads/products/${filename}`;
    
    console.log('ProductPage trying direct URL:', directUrl);
    return directUrl;
  };

  const fetchProduct = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get(`/api/products/${id}`);
      setProduct(response.data);
    } catch (error) {
      console.error('Error fetching product:', error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (id) {
      fetchProduct();
    }
  }, [id, fetchProduct]);

  useEffect(() => {
    if (!product) return;
    const hasColors = Array.isArray(product.colors) && product.colors.length > 0;
    if (!hasColors) {
      setSelectedColor('');
      return;
    }
    if (selectedColor && !product.colors?.includes(selectedColor)) {
      setSelectedColor('');
    }
  }, [product, selectedColor]);

  useEffect(() => {
    const fetchRecommendations = async () => {
      if (!product?.category) {
        setRecommendedProducts([]);
        return;
      }

      setLoadingRecommendations(true);
      try {
        const response = await api.get('/api/products', {
          params: {
            category: product.category,
            limit: 1000
          }
        });

        const list: Product[] = Array.isArray(response.data?.products) ? response.data.products : [];
        const filtered = list.filter((item) => item._id !== product._id);
        const shuffled = [...filtered].sort(() => Math.random() - 0.5);
        setRecommendedProducts(shuffled.slice(0, 5));
      } catch (error) {
        console.error('Error fetching recommendations:', error);
        setRecommendedProducts([]);
      } finally {
        setLoadingRecommendations(false);
      }
    };

    fetchRecommendations();
  }, [product?._id, product?.category]);

  const handleAddToCart = () => {
    if (product) {
      const hasColors = Array.isArray(product.colors) && product.colors.length > 0;
      if (hasColors && !selectedColor) {
        setColorError('يرجى اختيار اللون قبل الإضافة إلى السلة');
        return;
      }

      setColorError('');
      setIsAddingToCart(true);
      
      addItem({
        id: product._id,
        name: product.name,
        price: product.price,
        image: getImageUrl(product.images[0]),
        selectedColor: hasColors ? selectedColor : undefined
      });
      
      // Reset animation after 1.5 seconds
      setTimeout(() => {
        setIsAddingToCart(false);
      }, 1500);
    }
  };

  const showPreviousImage = () => {
    if (!product || product.images.length <= 1) return;
    setSelectedImage((prev) => (prev === 0 ? product.images.length - 1 : prev - 1));
  };

  const showNextImage = () => {
    if (!product || product.images.length <= 1) return;
    setSelectedImage((prev) => (prev === product.images.length - 1 ? 0 : prev + 1));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-pink-50 via-rose-50 to-pink-100 dark:bg-none dark:from-dark-bg dark:via-dark-bg dark:to-dark-bg transition-colors duration-300">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="animate-pulse">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div className="bg-gray-200 dark:bg-dark-surface rounded-lg h-96 transition-colors duration-300"></div>
              <div className="space-y-4">
                <div className="bg-gray-200 dark:bg-dark-surface h-8 rounded transition-colors duration-300"></div>
                <div className="bg-gray-200 dark:bg-dark-surface h-6 rounded transition-colors duration-300"></div>
                <div className="bg-gray-200 dark:bg-dark-surface h-4 rounded transition-colors duration-300"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-pink-50 via-rose-50 to-pink-100 dark:bg-none dark:from-dark-bg dark:via-dark-bg dark:to-dark-bg transition-colors duration-300">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-dark-text transition-colors duration-300">Product not found</h1>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-pink-50 via-rose-50 to-pink-100 dark:bg-none dark:from-dark-bg dark:via-dark-bg dark:to-dark-bg transition-colors duration-300">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Product Images */}
        <div className="space-y-4">
          <div className="relative w-full aspect-[3/4] overflow-hidden rounded-lg bg-pink-100 dark:bg-dark-hover transition-colors duration-300 flex items-center justify-center">
            <img
              src={getImageUrl(product.images[selectedImage])}
              alt={product.name}
              className="h-full w-full object-contain"
              onError={(e) => {
                console.log('ProductPage main image failed to load, using fallback');
                e.currentTarget.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgdmlld0JveD0iMCAwIDEwMCAxMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2Zz4KPHJlY3Qgd2lkdGg9IjEwMCIgaGVpZ2h0PSIxMDAiIGZpbGw9IiNCM0IzQjMiLz4KPHBhdGggZD0iTTMwIDMwSDcwVjcwSDMwVjMwWiIgZmlsbD0iI0QxRDVEQiIvPgo8cGF0aCBkPSJNNDAgNDBIMjBWNjBIMjBWNDBaIiBmaWxsPSIjQzdDQ0Q3Ii8+Cjwvc3ZnPgo=';
              }}
            />

            {product.images.length > 1 && (
              <>
                <button
                  type="button"
                  onClick={showNextImage}
                  className="absolute left-3 top-1/2 -translate-y-1/2 p-2 rounded-full bg-white/80 dark:bg-dark-surface/90 text-gray-800 dark:text-dark-text hover:bg-white dark:hover:bg-dark-hover shadow-md transition-colors duration-200"
                  aria-label="Previous image"
                >
                  <ChevronLeft className="h-5 w-5" />
                </button>
                <button
                  type="button"
                  onClick={showPreviousImage}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-2 rounded-full bg-white/80 dark:bg-dark-surface/90 text-gray-800 dark:text-dark-text hover:bg-white dark:hover:bg-dark-hover shadow-md transition-colors duration-200"
                  aria-label="Next image"
                >
                  <ChevronRight className="h-5 w-5" />
                </button>
              </>
            )}
          </div>
          
          {product.images.length > 1 && (
            <div className="grid grid-cols-4 gap-2">
              {product.images.map((image, index) => (
                <button
                  key={index}
                  onClick={() => setSelectedImage(index)}
                  className={`h-20 sm:h-24 overflow-hidden rounded-lg transition-all duration-200 flex items-center justify-center bg-pink-100 dark:bg-dark-hover ${
                    selectedImage === index ? 'ring-2 ring-primary-500' : ''
                  }`}
                >
                  <img
                    src={getImageUrl(image)}
                    alt={`${product.name} ${index + 1}`}
                    className="h-full w-full object-contain"
                    onError={(e) => {
                      console.log('ProductPage thumbnail failed to load, using fallback');
                      e.currentTarget.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgdmlld0JveD0iMCAwIDEwMCAxMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2Zz4KPHJlY3Qgd2lkdGg9IjEwMCIgaGVpZ2h0PSIxMDAiIGZpbGw9IiNCM0IzQjMiLz4KPHBhdGggZD0iTTMwIDMwSDcwVjcwSDMwVjMwWiIgZmlsbD0iI0QxRDVEQiIvPgo8cGF0aCBkPSJNNDAgNDBIMjBWNjBIMjBWNDBaIiBmaWxsPSIjQzdDQ0Q3Ii8+Cjwvc3ZnPgo=';
                    }}
                  />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Product Info */}
        <div className="space-y-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-dark-text transition-colors duration-300">{product.name}</h1>
            <p className="text-lg text-gray-600 dark:text-dark-textSecondary mt-2 transition-colors duration-300">{product.category}</p>
          </div>



                        <div className="text-3xl font-bold text-pink-600 dark:text-pink-400 transition-colors duration-300">₪{product.price}</div>

          <p className="text-gray-700 dark:text-gray-300 leading-relaxed transition-colors duration-300">{product.description}</p>

          {Array.isArray(product.colors) && product.colors.length > 0 && (
            <div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-dark-text mb-2 transition-colors duration-300">الألوان</h3>
              <div className="flex flex-wrap items-center gap-2">
                {product.colors.map((color) => {
                  const isSelected = selectedColor === color;
                  return (
                    <button
                      key={color}
                      type="button"
                      onClick={() => {
                        setSelectedColor(color);
                        setColorError('');
                      }}
                      className={`h-9 w-9 rounded-full border-2 transition-all ${isSelected ? 'border-pink-500 scale-110' : 'border-gray-300 dark:border-dark-border'}`}
                      style={{ backgroundColor: color }}
                      aria-label={`اختر اللون ${color}`}
                      title={color}
                    />
                  );
                })}
              </div>
              {colorError && <p className="mt-2 text-sm text-red-500">{colorError}</p>}
            </div>
          )}

          <div className="flex space-x-4">
            <button
              onClick={handleAddToCart}
              disabled={isAddingToCart}
              className={`flex-1 min-h-[56px] px-6 py-4 flex items-center justify-center gap-3 rounded-xl text-white text-lg font-semibold tracking-wide bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-600 hover:to-rose-600 shadow-lg hover:shadow-xl hover:scale-[1.01] active:scale-[0.99] transition-all duration-300 ${
                isAddingToCart ? 'from-green-600 to-green-700 hover:from-green-600 hover:to-green-700' : ''
              }`}
            >
              {isAddingToCart ? (
                <>
                  <Check className="h-6 w-6 animate-bounce" />
                  <span>تمت الإضافة!</span>
                </>
              ) : (
                <>
                  <ShoppingCart className="h-6 w-6" />
                  <span>إضافة إلى السلة</span>
                </>
              )}
            </button>
            {/* Wishlist disabled */}
          </div>

          <div className="mt-2 p-4 rounded-xl bg-white/70 dark:bg-dark-surface/70 border border-pink-100 dark:border-dark-border">
            <ul className="space-y-3">
              <li className="flex items-start text-base text-gray-700 dark:text-dark-textSecondary leading-relaxed">
                <span className="inline-block h-2 w-2 rounded-full bg-pink-500 mt-2 ml-2 shrink-0" />
                <span>توصيل لجميع المناطق الفلسطينية.</span>
              </li>
              <li className="flex items-start text-base text-gray-700 dark:text-dark-textSecondary leading-relaxed">
                <span className="inline-block h-2 w-2 rounded-full bg-pink-500 mt-2 ml-2 shrink-0" />
                <span>متابعة لحالة الطلبات على الرسائل.</span>
              </li>
              <li className="flex items-start text-base text-gray-700 dark:text-dark-textSecondary leading-relaxed">
                <span className="inline-block h-2 w-2 rounded-full bg-pink-500 mt-2 ml-2 shrink-0" />
                <span>خصومات للطلبات اكثر من 200 شيكل.</span>
              </li>
            </ul>
          </div>

          <div className="mt-4 lg:hidden">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-dark-text mb-3 transition-colors duration-300">
              منتجات نوصي بها
            </h3>

            {loadingRecommendations ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {[...Array(3)].map((_, idx) => (
                  <div key={idx} className="h-36 rounded-lg bg-white/70 dark:bg-dark-surface/70 border border-pink-100 dark:border-dark-border animate-pulse" />
                ))}
              </div>
            ) : recommendedProducts.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {recommendedProducts.map((item) => (
                  <Link
                    key={item._id}
                    to={`/product/${item._id}`}
                    className="rounded-lg overflow-hidden border border-pink-100 dark:border-dark-border bg-white dark:bg-dark-surface hover:shadow-md transition-all duration-200"
                  >
                    <div className="aspect-[3/4] bg-pink-100 dark:bg-dark-hover">
                      <img
                        src={getImageUrl(item.images?.[0])}
                        alt={item.name}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.currentTarget.src =
                            'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgdmlld0JveD0iMCAwIDEwMCAxMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2Zz4KPHJlY3Qgd2lkdGg9IjEwMCIgaGVpZ2h0PSIxMDAiIGZpbGw9IiNCM0IzQjMiLz4KPHBhdGggZD0iTTMwIDMwSDcwVjcwSDMwVjMwWiIgZmlsbD0iI0QxRDVEQiIvPgo8cGF0aCBkPSJNNDAgNDBIMjBWNjBIMjBWNDBaIiBmaWxsPSIjQzdDQ0Q3Ii8+Cjwvc3ZnPgo=';
                        }}
                      />
                    </div>
                    <div className="p-2">
                      <p className="text-xs sm:text-sm text-gray-900 dark:text-dark-text line-clamp-1">{item.name}</p>
                      <p className="text-sm font-bold text-pink-600 dark:text-pink-400 mt-1">₪{item.price}</p>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500 dark:text-dark-textSecondary">لا توجد منتجات مشابهة حالياً.</p>
            )}
          </div>

          {/* Features */}
          {product.features && product.features.length > 0 && (
            <div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-dark-text mb-3 transition-colors duration-300">Features</h3>
              <ul className="space-y-2">
                {product.features.map((feature, index) => (
                  <li key={index} className="flex items-center text-gray-600 dark:text-dark-textSecondary transition-colors duration-300">
                    <div className="w-2 h-2 bg-primary-600 dark:bg-primary-400 rounded-full mr-3 transition-colors duration-300"></div>
                    {feature}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Specifications */}
          {product.specifications && Object.keys(product.specifications).length > 0 && (
            <div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-dark-text mb-3 transition-colors duration-300">Specifications</h3>
              <dl className="space-y-2">
                {Object.entries(product.specifications).map(([key, value]) => (
                  <div key={key} className="flex justify-between py-2 border-b border-gray-100 dark:border-gray-700 transition-colors duration-300">
                    <dt className="text-gray-600 dark:text-dark-textSecondary capitalize transition-colors duration-300">{key}</dt>
                    <dd className="text-gray-900 dark:text-dark-text transition-colors duration-300">{String(value)}</dd>
                  </div>
                ))}
              </dl>
            </div>
          )}
        </div>
      </div>

      <div className="hidden lg:block max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8">
        <div className="mt-2">
          <h3 className="text-2xl font-semibold text-gray-900 dark:text-dark-text mb-4 transition-colors duration-300">
            منتجات نوصي بها
          </h3>

          {loadingRecommendations ? (
            <div className="grid grid-cols-3 xl:grid-cols-5 gap-4">
              {[...Array(5)].map((_, idx) => (
                <div key={idx} className="h-56 rounded-lg bg-white/70 dark:bg-dark-surface/70 border border-pink-100 dark:border-dark-border animate-pulse" />
              ))}
            </div>
          ) : recommendedProducts.length > 0 ? (
            <div className="grid grid-cols-3 xl:grid-cols-5 gap-4">
              {recommendedProducts.map((item) => (
                <Link
                  key={item._id}
                  to={`/product/${item._id}`}
                  className="rounded-lg overflow-hidden border border-pink-100 dark:border-dark-border bg-white dark:bg-dark-surface hover:shadow-md transition-all duration-200"
                >
                  <div className="aspect-[3/4] bg-pink-100 dark:bg-dark-hover">
                    <img
                      src={getImageUrl(item.images?.[0])}
                      alt={item.name}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.currentTarget.src =
                          'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgdmlld0JveD0iMCAwIDEwMCAxMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2Zz4KPHJlY3Qgd2lkdGg9IjEwMCIgaGVpZ2h0PSIxMDAiIGZpbGw9IiNCM0IzQjMiLz4KPHBhdGggZD0iTTMwIDMwSDcwVjcwSDMwVjMwWiIgZmlsbD0iI0QxRDVEQiIvPgo8cGF0aCBkPSJNNDAgNDBIMjBWNjBIMjBWNDBaIiBmaWxsPSIjQzdDQ0Q3Ii8+Cjwvc3ZnPgo=';
                      }}
                    />
                  </div>
                  <div className="p-2">
                    <p className="text-sm text-gray-900 dark:text-dark-text line-clamp-1">{item.name}</p>
                    <p className="text-sm font-bold text-pink-600 dark:text-pink-400 mt-1">₪{item.price}</p>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-500 dark:text-dark-textSecondary">لا توجد منتجات مشابهة حالياً.</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductPage;
